import React, { FC, useEffect, useRef, useState } from "react";
import {
  getSortedRowModel,
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { useAppSelector } from "../../../redux/hooks";
import Loader from "components/ui/Loader";
import NoData from "components/ui/NoData";
import "./Table.scss";
import Columns from "./Columns";
import useTable from "../../../hooks/UseTable";
import LinearLoader from "components/ui/LinearLoader";
import TableRow from "./Row";
import Const from "../../../const.json";
import CustomTooltip from "../CustomTooltip/CustomTooltip";

export interface MyTableProps {
  chosenCategoryIndex: number;
  optionId: string;
}
const Table: FC<MyTableProps> = ({ chosenCategoryIndex, optionId }) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState({});
  const [sorting, setSorting] = useState([]);
  const { period } = useAppSelector((state) => state.general);

  const tooltipContent = {
    justifiedComplaints: Const.tooltipContentJustifiedComplaints,
    unJustifiedComplaints: Const.tooltipContentUnjustifiedComplaints,
    complaintsResolvedWithoutAcknowledgment: Const.tooltipComplaintsResolvedWithoutAcknowledgment
  };

  const {
    data,
    flatData,
    isLastPage,
    fetchNextPage,
    isFetching,
    isLoading,
    isFetchingNextPage,
  } = useTable({ chosenCategoryIndex, period, optionId });

  useEffect(() => {
    setExpanded({});
  }, [chosenCategoryIndex, period, optionId]);

  const table = useReactTable({
    data: flatData,
    columns: Columns(),
    state: { sorting, expanded },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
    getRowCanExpand: (row) => row.original.children?.length > 0,
    getSubRows: (row) => row.children,
  });

  const { rows } = table.getRowModel();

  useEffect(() => {
    const scrollElement = tableContainerRef.current;
    const handleScroll = () => {
      if (!scrollElement) return;

      const { scrollHeight, scrollTop, clientHeight } = scrollElement;
      if (
        scrollHeight - scrollTop <= clientHeight + 1 &&
        !isFetching &&
        !isLastPage
      ) {
        fetchNextPage();
      }
    };

    scrollElement?.addEventListener("scroll", handleScroll);
    return () => scrollElement?.removeEventListener("scroll", handleScroll);
  }, [fetchNextPage, isFetching, flatData.length, isLastPage]);

  const handleKeyDownSort = (e, handler) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handler(e);
    }
  };

  return (
    <div>
      {(isLoading || isFetching) && !isFetchingNextPage ? (
        <Loader />
      ) : flatData.length === 0 || !data ? (
        <NoData />
      ) : (
        <div className="scroll-parent">
          <div
            className="table-container"
            ref={tableContainerRef}
            style={{ overflowY: "auto" }}
          >
            <table className="table table-striped table-bordered" role="table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                        const isSorted = header.column.getIsSorted();
                        const sortDirection = isSorted
                          ? header.column.getIsSorted() === "asc"
                            ? "ascending"
                            : "descending"
                          : undefined;
  
                      return(
                      <th
                        onKeyDown={(e) => handleKeyDownSort(e, header.column.getToggleSortingHandler())}
                        role="button"
                        key={header.id}
                        tabIndex={0}
                        aria-sort={sortDirection}
                        aria-label={`${
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )} ${
                          isSorted ? `sorted ${sortDirection}` : ""
                        }`}
                      >
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? "cursor-pointer select-none"
                              : "",
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {tooltipContent.hasOwnProperty(header.id) &&
                            (<CustomTooltip content={tooltipContent[header.id]} />)
                          }
                          {{
                            asc: <b className="m-2">&and;</b>,
                            desc: <b className="m-2">&or;</b>,
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>
                      </th>)
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {rows.map((row) => (
                  <TableRow row={row} key={row.id}/>
                ))}
              </tbody>
            </table>
            {isFetchingNextPage && <LinearLoader />}
          </div>
        </div>
      )}
    </div>
  );
};
export default Table;
